import { NgxPermissionsService } from 'ngx-permissions'
import { FetchSettingsRequest } from '@containers/settings/settings.actions'
import { UpdateField } from './../properties.actions'
import {
  getPropertyState,
  getCreatePropertyState,
} from './../properties.selectors'
import { Router, ActivatedRoute } from '@angular/router'
import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core'
import { Store, select } from '@ngrx/store'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription, Observable, Subject } from 'rxjs'
import { Map } from 'immutable'
import { getSettingsState } from '@containers/settings/settings.selectors'
import {
  DetectAllLayersRequest,
  DetectLayerRequest,
  FetchActiveLayersRequest,
  SearchCommunitiesRequest,
} from '@shared/shared.actions'
import { GeoJSON } from 'ol/format'
import { FormArray, FormBuilder, FormGroup } from '@angular/forms'
import {
  getActiveLayers,
  getDetectedLayer,
  getDetectedLayers,
} from '@shared/shared.select'
import { FetchBuildingRequest } from '@containers/digitization/digitization.actions'
import { MultiPolygon, Point } from 'ol/geom'
import { Feature } from 'ol'
import { roundLngLat } from '@shared/shared.utils'

@Component({
  selector: 'app-property-measure',
  templateUrl: './property-measure.component.html',
  styleUrls: ['./property-measure.component.css'],
})
export class PropertyMeasureComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  public propertyId: number
  private state$: Subscription
  public isLoading: boolean
  public isReadonly: boolean
  public status: string
  public nextStatus: string
  private parent: string
  public hasReadonlyPermission: boolean = false
  public hasPinEditPermission: boolean = false
  public hasPolygonEditPermission: boolean = false
  public hasAreaEditPermission: boolean = false
  public settings$: Subscription
  public settings: any = {}
  public layers: FormGroup
  private detectLayerState$: Subscription
  private detectAllLayersState$: Subscription
  private activeLayersState$: Subscription
  private feature: any
  private interval: any
  private layerFeaturesInitialized = false

  constructor(
    public permissionsService: NgxPermissionsService,
    private store: Store<any>,
    public router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
  ) {
    super()
  }

  ngOnInit() {
    this.layers = this.formBuilder.group({
      rows: this.formBuilder.array([]),
    })

    this.propertyId = parseInt(this.route.parent.snapshot.params.id, 10)
    this.hasReadonlyPermission =
      this.permissionsService.getPermissions()['READONLY'] !== undefined
    this.hasPinEditPermission =
      this.permissionsService.getPermissions()['EDIT_PIN'] !== undefined
    this.hasPolygonEditPermission =
      this.permissionsService.getPermissions()['EDIT_POLYGON'] !== undefined
    this.hasAreaEditPermission =
      this.permissionsService.getPermissions()['EDIT_AREA'] !== undefined

    this.parent = this.propertyId ? 'property' : 'createProperty'
    const getState = this.propertyId ? getPropertyState : getCreatePropertyState
    this.state$ = this.store.pipe(select(getState)).subscribe((state) => {
      this.state = state

      if (this.propertyId) {
        const status = this.state.getIn(['data', 'status', 'name'])
        this.isReadonly =
          status === 'BILLING' ||
          this.hasReadonlyPermission ||
          status === 'ARCHIVED'
      }

      const building = (this.state.getIn(['data', 'building']) as any)?.toJS()

      if (building?.feature) {
        const geometry = new MultiPolygon(building?.feature.coordinates)
        this.feature = new Feature({
          geometry: geometry,
          selectable: true,
        })
      } else if (building && building.pointLng && building.pointLat) {
        this.feature = new Feature({
          geometry: new Point([building.pointLng, building.pointLat]),
        })
      }
    })

    this.activeLayersState$ = this.store
      .pipe(select(getActiveLayers))
      .subscribe((state) => {
        const rows = this.layers.get('rows') as FormArray

        rows.clear()

        state.toJS().forEach((layer: any) => {
          const newLayers = (
            this.state.getIn(['buildingLayerFeatures']) as any
          )?.toJS()

          const value = newLayers?.find(({ layerId }) => layerId === layer.id)

          rows.push(
            this.formBuilder.group({
              name: [layer.name],
              id: [layer.id],
              features: [layer.features],
              feature: [value?.id ?? null],
              calculation: [layer?.calculation],
              value: [layer?.value],
            }),
          )
        })
      })

    this.detectLayerState$ = this.store
      .pipe(select(getDetectedLayer))
      .subscribe((state) => {
        if (!state.get('isLoading') && state.getIn(['data', 'layerId'])) {
          this.selectedDetectedLayer(
            state.getIn(['data', 'id']),
            state.getIn(['data', 'layerId']),
          )
        }
      })

    this.detectAllLayersState$ = this.store
      .pipe(select(getDetectedLayers))
      .subscribe((state) => {
        if (!state.get('isLoading')) {
          state
            .get('data')
            .toJS()
            .forEach((layer: any) => {
              this.selectedDetectedLayer(layer.featureId, layer.layerId)
            })
        }
      })

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        if (state.getIn(['data', 'units'])) {
          this.settings = state.getIn(['data', 'units']).toJS()
        }
      })

    this.interval = setInterval(() => {
      const building = (this.state.getIn(['data', 'building']) as any)?.toJS()

      if (building && Array.from(this.activeLayers).length) {
        clearInterval(this.interval)

        if (building?.buildingLayerFeatures && !this.layerFeaturesInitialized) {
          this.layerFeaturesInitialized = true
          building.buildingLayerFeatures.forEach((row: any) => {
            if (row.layerFeature) {
              this.selectedDetectedLayer(
                row.layerFeature.id,
                row.layerFeature.layerId,
              )
            }
          })
        }
      }
    }, 300)

    this.layers.valueChanges.subscribe(() => {
      const buildingLayerFeatures = this.activeLayers
        .map((row) => ({
          layerId: row.get('id').value,
          value: row
            .get('features')
            .value.find((f: any) => f.id === row.get('feature').value)?.value,
          calculation: row.get('calculation').value,
          id: row.get('feature').value,
        }))
        .filter(({ id }) => !!id)

      this.store.dispatch(
        new UpdateField(
          'buildingLayerFeatures',
          buildingLayerFeatures,
          this.parent,
        ),
      )
    })

    this.store.dispatch<any>(new FetchActiveLayersRequest())
    this.store.dispatch<any>(new SearchCommunitiesRequest(''))
  }

  get layerFeatures() {
    if (
      this.state.get('buildingLayerFeatures') &&
      this.state.get('buildingLayerFeatures').size
    ) {
      return this.state.get('buildingLayerFeatures').toJS()
    }

    if (this.getField(['building', 'buildingLayerFeatures'])) {
      return this.getField(['building', 'buildingLayerFeatures']).toJS()
    }

    return []
  }

  get polygon() {
    if (this.getField(['building', 'feature'])) {
      return this.getField(['building', 'feature']).toJS()
    }

    return null
  }

  ngOnDestroy() {
    this.state$.unsubscribe()

    if (this.settings$) {
      this.settings$.unsubscribe()
    }

    if (this.activeLayersState$) {
      this.activeLayersState$.unsubscribe()
    }

    if (this.detectLayerState$) {
      this.detectLayerState$.unsubscribe()
    }

    if (this.detectAllLayersState$) {
      this.detectAllLayersState$.unsubscribe()
    }
  }

  getSelectedFeatureById(fid: any, features: any[]) {
    const feature = features.find(({ id }) => id === fid)

    return feature ? feature.label : null
  }

  detect(id: string) {
    if (this.feature) {
      const json = new GeoJSON().writeGeometry(this.feature.getGeometry())

      this.store.dispatch<any>(
        new DetectLayerRequest({
          id,
          geoJson: JSON.parse(json),
        }),
      )
    }
  }

  detectAll() {
    if (this.feature) {
      const json = new GeoJSON().writeGeometry(this.feature.getGeometry())
      this.store.dispatch<any>(
        new DetectAllLayersRequest({
          geoJson: JSON.parse(json),
        }),
      )
    }
  }

  selectedDetectedLayer(id: any, layerId: any) {
    const layer = this.activeLayers.find(
      (l) => l.get('id').value === layerId,
    ) as FormGroup

    if (layer) {
      layer.get('feature').setValue(id)
    }
  }

  get activeLayers() {
    return this.layers.get('rows')['controls'] as FormGroup[]
  }

  onPolygonChange({ polygon, area }) {
    this.handleFieldChange(area, 'area', ['building'])
    this.handleFieldChange(polygon, 'feature', ['building'])
  }

  onMarkerChange(position: number[]) {
    this.handleFieldChange(roundLngLat(position[0]), 'pointLng', ['building'])
    this.handleFieldChange(roundLngLat(position[1]), 'pointLat', ['building'])
  }

  public handleFieldChange(
    e: any,
    name: null | string = null,
    path?: any,
  ): void {
    if (name === 'pointLat' || name === 'pointLng') {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          e || null,
          `${this.parent}.data${path ? `.${path.join('.')}` : ''}`,
        ),
      )
    } else {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name
            ? e
            : isNaN(e.target.value)
            ? e.target.value
            : e.target.value === ''
            ? null
            : parseFloat(e.target.value),
          `${this.parent}.data${path ? `.${path.join('.')}` : ''}`,
        ),
      )
    }

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state
          .get('validationErrors')
          .delete(
            path
              ? `${path.join()}.${name || e.target.name}`
              : name || e.target.name,
          ),
        this.parent,
      ),
    )
  }

  getCalculationValue(id: any) {
    const features = (this.state.get('buildingLayerFeatures') as any)?.toJS()
    const feature = features.find((row: any) => {
      if (row) {
        return row.id === id
      }

      return false
    })

    return feature && feature.value
  }
}
